import { Buffer } from 'buffer';
import { useMemo } from 'react';
import { BleError } from 'react-native-ble-plx';
import { useRobotStateStore } from '../stores/useRobotStateStore';
import { BluetoothConnectionStatus } from './types';

export const useBleConnection = (robotId: string | undefined) => {
  const bluetoothConnection = useRobotStateStore((state) =>
    robotId ? state.robotDevices[robotId]?.bluetoothConnection : undefined
  );

  const getBluetoothDevice = () => {
    if (!robotId) {
      return undefined;
    }

    // This is needed as sometimes the bluetoothDevice from the useRobotStateStore may not have been updated by the time this is called.
    let _bluetoothDevice = bluetoothConnection?.bluetoothDevice;
    if (_bluetoothDevice === undefined) {
      _bluetoothDevice =
        useRobotStateStore.getState().robotDevices[robotId]?.bluetoothConnection
          ?.bluetoothDevice;
    }

    return _bluetoothDevice;
  };

  const connectionStatus = useMemo(
    () => bluetoothConnection?.connectionStatus ?? 'disconnected',
    [bluetoothConnection?.connectionStatus]
  );

  const setConnectionStatus = (newStatus: BluetoothConnectionStatus) => {
    if (robotId && bluetoothConnection) {
      console.log('Setting new state to', newStatus);
      useRobotStateStore.setState((state) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state.robotDevices[robotId].bluetoothConnection!.connectionStatus =
          newStatus;
      });
    } else {
      console.log(
        'Would have set new state but....',
        robotId,
        bluetoothConnection
      );
    }
  };

  /**
   * Disconnects from the bluetooth robot device.
   *
   * @returns boolean indicating whether robot is disconnected or not
   */
  const disconnect = async (params?: { onError?: (err: unknown) => void }) => {
    setConnectionStatus('disconnecting');

    const bluetoothDevice = getBluetoothDevice();

    try {
      await bluetoothDevice?.cancelConnection();
    } catch (err) {
      console.log('Error in disconnecting from device', err);
      params?.onError?.(err);
    }

    const isConnected = (await bluetoothDevice?.isConnected()) ?? false;
    setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    return !isConnected;
  };

  /**
   * Attempts to read the value of a characteristic
   *
   * @returns Value of characteristic, or undefined if no value or it could not be reads
   */
  const readCharacteristic = async (
    serviceUUID: string,
    charUUID: string,
    params?: { onError?: (err: unknown) => void }
  ) => {
    const bluetoothDevice = getBluetoothDevice();

    try {
      const char = await bluetoothDevice?.readCharacteristicForService(
        serviceUUID,
        charUUID
      );

      if (char?.value) {
        return Buffer.from(char.value, 'base64').toString();
      } else {
        return undefined;
      }
    } catch (err) {
      console.log('Error in reading characteristics', err);
      params?.onError?.(err);
    }

    return undefined;
  };

  /**
   * Attempts to write a value to a characteristic
   *
   * @returns boolean indicating success
   */
  const writeCharacteristic = async (
    serviceUUID: string,
    charUUID: string,
    value: string,
    params?: { onError?: (err: unknown) => void }
  ) => {
    const bluetoothDevice = getBluetoothDevice();

    try {
      await bluetoothDevice?.writeCharacteristicWithResponseForService(
        serviceUUID,
        charUUID,
        Buffer.from(value).toString('base64')
      );
      return true;
    } catch (err) {
      console.log('Error in writing characteristics', err);
      params?.onError?.(err);
    }

    return false;
  };

  /**
   * Attempts to monitor the value of a characteristic.
   * Used when the characteristic supports notifications or indications.
   * The callback passed in is called with the decoded value if it exists, or null otherwise.s
   *
   * @returns value a subscription, on which `.remove()` can be used to remove the monitor
   */
  const monitorCharacteristic = (
    serviceUUID: string,
    charUUID: string,
    callback: (error: BleError | null, value: string | null) => void
  ) => {
    const bluetoothDevice = getBluetoothDevice();

    return bluetoothDevice?.monitorCharacteristicForService(
      serviceUUID,
      charUUID,
      (error, char) => {
        let decodedVal = null;
        if (char?.value) {
          decodedVal = Buffer.from(char.value, 'base64').toString();
        }
        callback(error, decodedVal);
      }
    );
  };

  return {
    disconnect,
    readCharacteristic,
    writeCharacteristic,
    monitorCharacteristic,
    connectionStatus,
    setConnectionStatus,
  };
};
